'use client';

import { useEffect, useRef, type MutableRefObject } from 'react';
import { tw } from '@tw';

interface GoogleSignInHandlers {
  onSuccess: (idToken: string) => void;
  onError?: (error: string) => void;
}

interface GoogleSignInButtonProps extends GoogleSignInHandlers {
  className?: string;
  /** GIS button theme — pick the one that sits in the surface's palette:
      `filled_black` on light cards, `outline` (white) on accent grounds. */
  theme?: 'outline' | 'filled_black';
}

// GIS keeps ONE global credential callback (whatever `initialize` saw last), but
// several sign-in forms can be mounted at once — the sign-in pill and a product
// card's inline form, for instance. Initialize once with a dispatcher and route
// the credential to the button that was clicked (falling back to the last one
// mounted), so a card never receives the pill's sign-in or vice versa.
let activeHandlers: MutableRefObject<GoogleSignInHandlers> | null = null;
let gisInitialized = false;

function dispatchCredential(response: google.accounts.id.CredentialResponse) {
  const handlers = activeHandlers?.current;
  if (!handlers) return;
  if (response.credential) {
    handlers.onSuccess(response.credential);
  } else {
    handlers.onError?.('No credential received from Google');
  }
}

export function GoogleSignInButton({
  onSuccess,
  onError,
  className,
  theme = 'outline',
}: GoogleSignInButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);
  const handlersRef = useRef<GoogleSignInHandlers>({ onSuccess, onError });

  useEffect(() => {
    handlersRef.current = { onSuccess, onError };
  }, [onSuccess, onError]);

  useEffect(() => {
    if (renderedRef.current || !buttonRef.current) return;

    const clientId = process.env.NEXT_PUBLIC_GOOGLE_SIGN_IN_CLIENT_ID;
    if (!clientId) {
      console.error('NEXT_PUBLIC_GOOGLE_SIGN_IN_CLIENT_ID is not set');
      return;
    }

    const initializeButton = () => {
      if (!window.google || !buttonRef.current || renderedRef.current) return;

      renderedRef.current = true;

      if (!gisInitialized) {
        gisInitialized = true;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: dispatchCredential,
        });
      }
      activeHandlers = handlersRef;

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme,
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        width: buttonRef.current.offsetWidth || 280,
        click_listener: () => {
          activeHandlers = handlersRef;
        },
      });
    };

    // Check if script already loaded
    if (window.google) {
      initializeButton();
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeButton();
        }
      }, 100);

      // Cleanup after 10 seconds
      setTimeout(() => clearInterval(checkGoogle), 10000);

      return () => clearInterval(checkGoogle);
    }
    // renderedRef guards re-runs, so a theme change after first render is a
    // no-op by design — GIS buttons don't restyle in place.
  }, [theme]);

  // Fixed, clipped height: while GIS injects the button it briefly stacks a
  // placeholder above the iframe (~80px) before collapsing to the final 44px.
  // A min-height would let that transient grow the modal for a frame (a visible
  // height "pump"); a fixed, overflow-hidden box keeps the modal layout stable.
  //
  // color-scheme:light — color-scheme now follows the site theme (the OS
  // decides unless stamped), so the active scheme can be dark, and a browser paints a
  // cross-origin iframe's backdrop opaque white when the embedder's
  // color-scheme differs from the iframe's (which is light). That backdrop
  // is a white frame around the button on any tinted surface; scoping this
  // container to light keeps the iframe backdrop transparent.
  return (
    <div
      ref={buttonRef}
      className={tw(
        'w-full h-[44px] overflow-hidden flex items-center justify-center [&>div]:!w-full [color-scheme:light]',
        className
      )}
    />
  );
}
